import { Button, Checkbox, Input, Modal, Select, Textarea } from '../../components';
import { useLang } from '../../i18n';
import ChannelChip from './components/ChannelChip';
import ChannelInput from './components/ChannelInput';
import ServiceCombobox from './components/ServiceCombobox';
import SuccessState from './components/SuccessState';
import { useLeadForm, type ChannelKind } from './hooks/useLeadForm';
import './LeadFormModal.scss';

export interface LeadFormModalProps {
  open: boolean;
  onClose: () => void;
  defaultServiceSlug?: string;
}

const CHANNEL_ORDER: ChannelKind[] = ['telegram', 'whatsapp', 'phone', 'email'];

export default function LeadFormModal({ open, onClose, defaultServiceSlug }: LeadFormModalProps) {
  const { t } = useLang();
  const form = useLeadForm({ open, onClose, defaultServiceSlug });

  return (
    <Modal open={open} onClose={onClose} title={t('lead.title')} size="md">
      {form.submitted ? (
        <SuccessState
          title={t('lead.success.t')}
          description={t('lead.success.d')}
          telegramUrl="https://t.me/mrbek112"
          whatsappUrl="https://wa.me/998901234567"
          onClose={onClose}
          labels={{ telegram: t('lead.success.tg'), whatsapp: t('lead.success.wa'), close: t('lead.success.close') }}
        />
      ) : (
        <form className="lead-form" onSubmit={form.submit} noValidate>
          <Input
            label={t('lead.name')}
            value={form.name}
            onChange={(e) => form.setName(e.target.value)}
            placeholder={t('lead.name.ph')}
            required
            minLength={2}
            autoComplete="name"
            error={form.errors.name}
          />

          <ServiceCombobox
            label={t('lead.service')}
            value={form.service}
            onChange={form.setService}
            options={form.services}
            placeholder={t('lead.service.ph')}
            emptyHint={t('lead.service.empty')}
          />

          <div className="lead-form__channels">
            <span className="lead-form__channels-label">{t('lead.channels.title')}</span>
            <div className="lead-form__chips" role="group" aria-label={t('lead.channels.title')}>
              {CHANNEL_ORDER.map((k) => (
                <ChannelChip
                  key={k}
                  kind={k}
                  label={t(`lead.channel.${k}`)}
                  active={form.channels.includes(k)}
                  onToggle={form.toggleChannel}
                />
              ))}
            </div>
            <div className="lead-form__fields">
              {CHANNEL_ORDER.map((k) => (
                <ChannelInput
                  key={k}
                  kind={k}
                  visible={form.channels.includes(k)}
                  value={form.values[k]}
                  onChange={(raw) => form.setChannelValue(k, raw)}
                  error={form.errors[`ch_${k}`]}
                />
              ))}
            </div>
          </div>

          <Textarea
            label={t('lead.description')}
            value={form.description}
            onChange={(e) => form.setDescription(e.target.value)}
            placeholder={t('lead.description.ph')}
            rows={4}
            maxHeight={240}
            error={form.errors.description}
          />

          <Select
            label={t('lead.budget')}
            value={form.budget}
            onChange={form.setBudget}
            options={form.budgetOptions}
            placeholder={t('lead.budget.ph')}
          />

          <Checkbox
            label={
              <span>
                {t('lead.agree.t')}{' '}
                <a href="/legal/privacy" target="_blank" rel="noreferrer noopener" className="lead-form__legal-link">
                  {t('lead.agree.link')}
                </a>
              </span>
            }
            checked={form.agree}
            onChange={(e) => form.setAgree(e.target.checked)}
            error={form.errors.agree}
          />

          <input
            className="lead-form__honey"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.honey}
            onChange={(e) => form.setHoney(e.target.value)}
            aria-hidden="true"
          />

          <Button type="submit" variant="primary" size="lg" loading={form.loading} fullWidth>
            {t('lead.submit')}
          </Button>
        </form>
      )}
    </Modal>
  );
}
